/** @format */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
    submitRegisterBusinessAdmin,
    submitRegisterTeamMember,
    submitVerifyBusinessUser,
    submitResendBusinessCode,
    submitForgotBusinessPassword,
    submitResetBusinessPassword,
    submitLoginBusinessUser,
    submitLogoutBusinessUser,
    readCachedBusinessUser,
    hasBusinessSession,
    IRegisterAdminPayload,
    IRegisterTeamPayload,
    IBusinessUserProfile,
    IBusinessAuthMembership,
} from "../services/businessAuthService";

// ─────────────────────────────────────────────────────────────────────────
// Collect BUSINESS auth slice — manages the dedicated BusinessUser session,
// fully isolated from the circle `user` slice.
// ─────────────────────────────────────────────────────────────────────────

interface BusinessAuthState {
    businessUser: IBusinessUserProfile | null;
    memberships: IBusinessAuthMembership[];
    isAuthenticated: boolean;

    isRegistering: boolean;
    registerError: string | null;
    // email pending verification (carried to the verify screen)
    pendingEmail: string | null;

    isVerifying: boolean;
    verifyError: string | null;

    // Forgot / reset password (unauthenticated, code-based)
    isRequestingReset: boolean;
    resetRequestError: string | null;
    resetCodeSent: boolean;
    isResettingPassword: boolean;
    resetPasswordError: string | null;

    isLoggingIn: boolean;
    loginError: string | null;
}

const initialState: BusinessAuthState = {
    businessUser: readCachedBusinessUser(),
    memberships: [],
    isAuthenticated: hasBusinessSession(),
    isRegistering: false,
    registerError: null,
    pendingEmail: null,
    isVerifying: false,
    verifyError: null,
    isRequestingReset: false,
    resetRequestError: null,
    resetCodeSent: false,
    isResettingPassword: false,
    resetPasswordError: null,
    isLoggingIn: false,
    loginError: null,
};

export const registerBusinessAdmin = createAsyncThunk(
    "businessAuth/registerAdmin",
    async (payload: IRegisterAdminPayload, { rejectWithValue }) => {
        try {
            const result = await submitRegisterBusinessAdmin(payload);
            return { email: payload.email, result };
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to create account.");
        }
    },
);

export const registerTeamMember = createAsyncThunk(
    "businessAuth/registerTeam",
    async (payload: IRegisterTeamPayload, { rejectWithValue }) => {
        try {
            const result = await submitRegisterTeamMember(payload);
            return { email: payload.email, result };
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to create account.");
        }
    },
);

export const verifyBusinessUser = createAsyncThunk(
    "businessAuth/verify",
    async (
        { email, code }: { email: string; code: string },
        { rejectWithValue },
    ) => {
        try {
            return await submitVerifyBusinessUser(email, code);
        } catch (e: any) {
            return rejectWithValue(e.message || "Verification failed.");
        }
    },
);

export const resendBusinessCode = createAsyncThunk(
    "businessAuth/resendCode",
    async (email: string, { rejectWithValue }) => {
        try {
            return await submitResendBusinessCode(email);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to resend code.");
        }
    },
);

export const requestBusinessPasswordReset = createAsyncThunk(
    "businessAuth/forgotPassword",
    async (email: string, { rejectWithValue }) => {
        try {
            await submitForgotBusinessPassword(email);
            return { email };
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to start password reset.",
            );
        }
    },
);

export const confirmBusinessPasswordReset = createAsyncThunk(
    "businessAuth/resetPassword",
    async (
        {
            email,
            code,
            newPassword,
        }: { email: string; code: string; newPassword: string },
        { rejectWithValue },
    ) => {
        try {
            return await submitResetBusinessPassword(email, code, newPassword);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to reset password.");
        }
    },
);

export const loginBusinessUser = createAsyncThunk(
    "businessAuth/login",
    async (
        { identifier, password }: { identifier: string; password: string },
        { rejectWithValue },
    ) => {
        try {
            return await submitLoginBusinessUser(identifier, password);
        } catch (e: any) {
            return rejectWithValue(e.message || "Sign in failed.");
        }
    },
);

export const logoutBusinessUser = createAsyncThunk(
    "businessAuth/logout",
    async () => {
        await submitLogoutBusinessUser();
    },
);

const businessAuthSlice = createSlice({
    name: "businessAuth",
    initialState,
    reducers: {
        clearBusinessAuthErrors(state) {
            state.registerError = null;
            state.verifyError = null;
            state.loginError = null;
            state.resetRequestError = null;
            state.resetPasswordError = null;
        },
        // Reset the forgot/reset-password sub-flow back to its initial state
        // (used when the user leaves or re-opens the Forgot Password screen).
        clearResetPasswordState(state) {
            state.isRequestingReset = false;
            state.resetRequestError = null;
            state.resetCodeSent = false;
            state.isResettingPassword = false;
            state.resetPasswordError = null;
        },
        setPendingEmail(state, action: PayloadAction<string | null>) {
            state.pendingEmail = action.payload;
        },
        // Keep the cached business profile name in sync after a self-service
        // profile edit (My Account modal), so the rest of the UI reflects it.
        setBusinessUserName(
            state,
            action: PayloadAction<{ firstName: string; lastName: string }>,
        ) {
            if (state.businessUser) {
                state.businessUser = {
                    ...state.businessUser,
                    firstName: action.payload.firstName,
                    lastName: action.payload.lastName,
                };
            }
        },
    },
    extraReducers: (builder) => {
        // ── Register (admin) ──────────────────────────────────────────────
        builder
            .addCase(registerBusinessAdmin.pending, (state) => {
                state.isRegistering = true;
                state.registerError = null;
            })
            .addCase(registerBusinessAdmin.fulfilled, (state, action) => {
                state.isRegistering = false;
                state.pendingEmail = action.payload.email;
            })
            .addCase(registerBusinessAdmin.rejected, (state, action) => {
                state.isRegistering = false;
                state.registerError = action.payload as string;
            });

        // ── Register (team member) ────────────────────────────────────────
        builder
            .addCase(registerTeamMember.pending, (state) => {
                state.isRegistering = true;
                state.registerError = null;
            })
            .addCase(registerTeamMember.fulfilled, (state, action) => {
                state.isRegistering = false;
                state.pendingEmail = action.payload.email;
            })
            .addCase(registerTeamMember.rejected, (state, action) => {
                state.isRegistering = false;
                state.registerError = action.payload as string;
            });

        // ── Verify ────────────────────────────────────────────────────────
        builder
            .addCase(verifyBusinessUser.pending, (state) => {
                state.isVerifying = true;
                state.verifyError = null;
            })
            .addCase(verifyBusinessUser.fulfilled, (state) => {
                state.isVerifying = false;
            })
            .addCase(verifyBusinessUser.rejected, (state, action) => {
                state.isVerifying = false;
                state.verifyError = action.payload as string;
            });

        // ── Forgot password (request a reset code) ────────────────────────
        builder
            .addCase(requestBusinessPasswordReset.pending, (state) => {
                state.isRequestingReset = true;
                state.resetRequestError = null;
            })
            .addCase(requestBusinessPasswordReset.fulfilled, (state, action) => {
                state.isRequestingReset = false;
                state.resetCodeSent = true;
                state.pendingEmail = action.payload.email;
            })
            .addCase(requestBusinessPasswordReset.rejected, (state, action) => {
                state.isRequestingReset = false;
                state.resetRequestError = action.payload as string;
            });

        // ── Reset password (confirm code + new password) ──────────────────
        builder
            .addCase(confirmBusinessPasswordReset.pending, (state) => {
                state.isResettingPassword = true;
                state.resetPasswordError = null;
            })
            .addCase(confirmBusinessPasswordReset.fulfilled, (state) => {
                state.isResettingPassword = false;
            })
            .addCase(confirmBusinessPasswordReset.rejected, (state, action) => {
                state.isResettingPassword = false;
                state.resetPasswordError = action.payload as string;
            });

        // ── Login ─────────────────────────────────────────────────────────
        builder
            .addCase(loginBusinessUser.pending, (state) => {
                state.isLoggingIn = true;
                state.loginError = null;
            })
            .addCase(loginBusinessUser.fulfilled, (state, action) => {
                state.isLoggingIn = false;
                state.isAuthenticated = true;
                state.businessUser = action.payload.businessUser;
                state.memberships = action.payload.memberships;
                state.pendingEmail = null;
            })
            .addCase(loginBusinessUser.rejected, (state, action) => {
                state.isLoggingIn = false;
                state.loginError = action.payload as string;
            });

        // ── Logout ────────────────────────────────────────────────────────
        builder.addCase(logoutBusinessUser.fulfilled, (state) => {
            state.isAuthenticated = false;
            state.businessUser = null;
            state.memberships = [];
        });
    },
});

export const {
    clearBusinessAuthErrors,
    clearResetPasswordState,
    setPendingEmail,
    setBusinessUserName,
} = businessAuthSlice.actions;

export default businessAuthSlice.reducer;
